/**
 * Picker de référentiel Qualité (QUAL-001 §5.3) — autocomplete + création à la
 * volée + édition/suppression inline. Cloné de `LeadTypePicker`, paramétré par
 * `refType`. Réutilise `ReferentialItemList` + `ReferentialPickerActions`.
 *
 * ⚠️ La valeur manipulée est le **code** (slug) — la NC stocke `origin_code`,
 * `vector_code`, etc. — pas l'`id`. On traduit code↔id pour la liste cmdk.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ChevronDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Command, CommandInput, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ReferentialItemList } from '@/components/shared/_referential-picker/ReferentialItemList'
import { ReferentialPickerActions } from '@/components/shared/_referential-picker/ReferentialPickerActions'
import { QualityReferentialDeleteDialog } from '@/components/quality/QualityReferentialDeleteDialog'
import { QualityReferentialEditDialog } from '@/components/quality/QualityReferentialEditDialog'
import { useAuth } from '@/contexts/AuthContext'
import { extractApiError } from '@/lib/api-error'
import { cn } from '@/lib/utils'
import {
    useCreateQualityReferential,
    useQualityReferentials,
} from '@/hooks/useQualityReferentials'
import type { QualityReferentialItem, QualityReferentialType } from '@/hooks/useQuality.types'

interface Props {
    entityId: string | null | undefined
    refType: QualityReferentialType
    value: string | null | undefined // code (slug)
    /**
     * Libellé porté par la FICHE (`nc.origin_label`…). Il fait foi quand le code
     * courant n'est plus dans la liste — une valeur retirée depuis. Sans lui, le
     * sélecteur affichait « Sélectionner… » sur une fiche dont la colonne du
     * registre montrait bel et bien sa classification.
     */
    currentLabel?: string | null
    onChange: (code: string | null) => void
    disabled?: boolean
    placeholderKey?: string
}

export function QualityReferentialPicker({
    entityId,
    refType,
    value,
    currentLabel,
    onChange,
    disabled,
    placeholderKey,
}: Props) {
    const { t } = useTranslation()
    const { hasPermission } = useAuth()
    const canManage = hasPermission('quality:manage')

    const { data, isLoading } = useQualityReferentials(entityId, refType)
    const createMutation = useCreateQualityReferential(entityId)

    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [editTarget, setEditTarget] = useState<QualityReferentialItem | null>(null)
    const [deleteTarget, setDeleteTarget] = useState<QualityReferentialItem | null>(null)

    // Le serveur ne renvoie QUE les valeurs actives (contrat du sélecteur de saisie,
    // seule surface de gestion depuis l'arbitrage PO) : filtrer ici sur `is_active`
    // ne pouvait donc rien retenir de plus. Le repli sur la valeur courante ne vient
    // pas de la liste — il vient de la FICHE, via `currentLabel`.
    const items = data?.items ?? []
    const selected = value ? items.find((i) => i.code === value) ?? null : null
    const triggerLabel = selected
        ? selected.label
        : value
          ? (currentLabel ?? value)
          : t(placeholderKey ?? 'quality.referentials.select', 'Sélectionner…')
    const byId = (id: string) => items.find((i) => i.id === id) ?? null

    const trimmedSearch = search.trim()
    const filtered = trimmedSearch
        ? items.filter((it) => it.label.toLowerCase().includes(trimmedSearch.toLowerCase()))
        : items
    const exactMatch = trimmedSearch
        ? items.some((it) => it.label.toLowerCase() === trimmedSearch.toLowerCase())
        : false
    const showCreateButton = canManage && trimmedSearch.length >= 2 && !exactMatch

    // Adapte les items {id,label} → {id,name} attendu par ReferentialItemList, en
    // CONSERVANT l'héritage : jeté ici, il faisait afficher crayon et corbeille sur
    // des valeurs du siège, et le serveur répondait « Référentiel introuvable » pour
    // une valeur visible un pixel au-dessus.
    const listItems = filtered.map((i) => ({
        id: i.id,
        name: i.label,
        inherited: i.is_inherited,
        inheritedFrom: i.entity_name,
    }))

    function handleSelectCode(code: string | null) {
        onChange(code)
        setOpen(false)
        setSearch('')
    }

    async function handleCreate() {
        if (!trimmedSearch) return
        try {
            const created = await createMutation.mutateAsync({ ref_type: refType, label: trimmedSearch })
            handleSelectCode(created.code)
        } catch (err) {
            toast.error(t('quality.referentials.createError', 'Impossible de créer la valeur'), {
                description: extractApiError(err),
            })
        }
    }

    return (
        <>
            <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled || isLoading}
                        className={cn(
                            'w-full justify-between font-normal',
                            !selected && 'text-muted-foreground',
                        )}
                    >
                        <span className="truncate">{triggerLabel}</span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    // Largeur ≥ trigger mais élargie à ~18rem : les libellés de
                    // référentiel (« Rapport d'accompagnement »…) ne sont plus tronqués
                    // dans les colonnes étroites de la modale. Radix gère la collision
                    // (décalage) pour rester dans le viewport.
                    className="w-72 min-w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                >
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder={t('quality.referentials.searchPlaceholder', 'Rechercher ou créer…')}
                            value={search}
                            onValueChange={setSearch}
                        />
                        <CommandList className="max-h-[min(60vh,22rem)]">
                            {!isLoading && filtered.length === 0 && (
                                <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                                    {trimmedSearch
                                        ? t('common.noResults', 'Aucun résultat.')
                                        : t('quality.referentials.empty', 'Aucune valeur. Tapez pour en créer une.')}
                                </div>
                            )}
                            <ReferentialItemList
                                items={listItems}
                                value={selected?.id ?? null}
                                onSelect={(id) => {
                                    const it = byId(id)
                                    if (it) handleSelectCode(it.code)
                                }}
                                canManage={canManage}
                                onEdit={(it) => {
                                    setEditTarget(byId(it.id))
                                    setOpen(false)
                                }}
                                onDelete={(it) => {
                                    setDeleteTarget(byId(it.id))
                                    setOpen(false)
                                }}
                            />
                        </CommandList>
                        <ReferentialPickerActions
                            showCreateButton={showCreateButton}
                            showClearButton={Boolean(value)}
                            onCreate={handleCreate}
                            onClear={() => handleSelectCode(null)}
                            isCreating={createMutation.isPending}
                            trimmedSearch={trimmedSearch}
                            createLabelKey="quality.referentials.create"
                            creatingLabelKey="common.creating"
                            clearLabelKey="quality.referentials.clear"
                            createLabelFallback='Créer « {{name}} »'
                            creatingLabelFallback="Création…"
                            clearLabelFallback="Effacer la sélection"
                        />
                    </Command>
                </PopoverContent>
            </Popover>
            {canManage && (
                <>
                    <QualityReferentialEditDialog
                        target={editTarget}
                        entityId={entityId ?? null}
                        onClose={() => setEditTarget(null)}
                    />
                    <QualityReferentialDeleteDialog
                        target={deleteTarget}
                        refType={refType}
                        entityId={entityId ?? null}
                        // `onClose` = fermeture, quelle qu'en soit la cause (Annuler,
                        // Échap, clic hors modale). Y vider la classification effaçait
                        // — et PERSISTAIT — l'origine de la fiche ouverte alors que rien
                        // n'avait été supprimé ; et sur le chemin nominal avec
                        // réaffectation, cela écrasait derrière le serveur la valeur
                        // qu'il venait de réaffecter. Seul `onDeleted` vide le champ,
                        // et uniquement si aucune réaffectation n'a pris le relais.
                        onClose={() => setDeleteTarget(null)}
                        onDeleted={(reassignedTo) => {
                            if (deleteTarget?.code === value) onChange(reassignedTo)
                            setDeleteTarget(null)
                        }}
                    />
                </>
            )}
        </>
    )
}
